import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { useCreateMouldMutation } from "@/services/backend/mould/service";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Mould } from "@/services/backend/mould/mould";
import { Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";

const FormSchema = z.object({
  description: z.string(),
  isYeast: z.boolean(),
});

interface EditMouldDialogProps {
  mould: Mould;
}

export function EditMouldDialog({ mould }: EditMouldDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [createMould, { isLoading, isSuccess, error }] =
    useCreateMouldMutation();
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      description: mould.description,
      isYeast: mould.isYeast,
    },
  });

  useEffect(() => {
    function reset(): void {
      if (isSuccess) {
        form.reset();
        setIsOpen(false);
      }
    }

    reset();
  }, [form, isSuccess]);

  function onSubmit(data: z.infer<typeof FormSchema>) {
    createMould({
      mould: {
        id: mould.id,
        originalId: mould.originalId,
        description: data.description,
        isYeast: data.isYeast,
      },
    });
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Pencil />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Neuen Schimmelpilz anlegen</DialogTitle>
          <DialogDescription>
            Bitte definieren Sie den Namen des Schimmelpilzes
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <div className="flex items-center space-x-2">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="w-full space-y-6"
            >
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Thema</FormLabel>
                    <FormControl>
                      <Input
                        disabled={isLoading}
                        placeholder="Beschreibung"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Beschreibung des Schimmelpilzes
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isYeast"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-y-0 space-x-3 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        disabled={isLoading}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Hefe</FormLabel>
                      <FormDescription>
                        Handelt es sich um Hefe?
                      </FormDescription>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              <Button disabled={isLoading} type="submit">
                Submit
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
